/**
 * Iter148 — Öffentliche Datenschutzerklärung (für Meta App-Review).
 */
import React from "react";
import { Link } from "react-router-dom";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="text-lg font-semibold text-slate-900">
            Buchhaltung CH
          </Link>
          <nav className="text-sm space-x-4 text-slate-600">
            <Link to="/privacy" className="hover:text-slate-900">Datenschutz</Link>
            <Link to="/terms" className="hover:text-slate-900">AGB</Link>
            <Link to="/data-deletion" className="hover:text-slate-900">Datenlöschung</Link>
            <Link to="/login" className="text-indigo-600 hover:text-indigo-700">Anmelden</Link>
          </nav>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 prose prose-slate">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Datenschutzerklärung</h1>
        <p className="text-sm text-slate-500 mb-8">Zuletzt aktualisiert: Juli 2026</p>

        <section className="space-y-6 text-slate-700 leading-relaxed">
          <p>
            Diese Datenschutzerklärung informiert Sie darüber, wie Buchhaltung CH
            (nachfolgend &bdquo;wir&ldquo;) personenbezogene Daten erhebt, verarbeitet und
            schützt. Wir halten uns an das Schweizer Datenschutzgesetz (DSG,
            revDSG 2023) sowie – wenn zutreffend – die EU-DSGVO.
          </p>

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">1. Verantwortliche Stelle</h2>
          <p>
            Buchhaltung CH · c/o Strongfit GmbH · Schweiz<br />
            E-Mail: <a href="mailto:info@ch-buchhaltung.ch" className="text-indigo-600">info@ch-buchhaltung.ch</a>
          </p>
          <p>
            Softwareentwicklung, Betrieb und Lizenzierung der Plattform durch die{" "}
            <a href="https://www.invictaflow.ch" target="_blank" rel="noopener noreferrer" className="text-indigo-600">
              InvictaX Group GmbH
            </a>.
          </p>

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">2. Erhobene Daten</h2>
          <p>Wir verarbeiten folgende Datenkategorien:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Kontodaten:</strong> Name, E-Mail-Adresse, Passwort-Hash (bcrypt), Rolle.</li>
            <li><strong>Firmendaten:</strong> Firmenname, UID, Adresse, IBAN, MWSt-Nummer.</li>
            <li><strong>Belege &amp; Buchhaltungsdaten:</strong> Hochgeladene Quittungen, Rechnungen, Journal-Einträge, Bankdaten (camt.053).</li>
            <li><strong>WhatsApp-Metadaten:</strong> Telefonnummer, Nachrichten-Content, Mediendateien (wenn du unseren WhatsApp-Bot nutzt — siehe §5).</li>
            <li><strong>Nutzungsdaten:</strong> Log-Ins, IP-Adresse, Zeitstempel, User-Agent (nur zur Missbrauchs-Erkennung).</li>
          </ul>

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">3. Zweck der Verarbeitung</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>Bereitstellung der Buchhaltungssoftware und aller Funktionen.</li>
            <li>Automatische Belegerfassung mit OCR (Google Cloud Vision) und KI-Analyse (Anthropic / OpenAI / Gemini).</li>
            <li>Bank-Abgleich, Mahnwesen, Steuerberichte.</li>
            <li>Kontaktaufnahme mit dir per E-Mail (Rechnungen, Erinnerungen, System-Benachrichtigungen).</li>
          </ul>

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">4. Drittanbieter (Auftragsverarbeiter)</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Meta Platforms Ireland Ltd</strong> (WhatsApp Business API) — nur wenn du unseren WhatsApp-Bot nutzt.</li>
            <li><strong>Google Cloud Vision</strong> (Schweiz-Region) — OCR-Erkennung von Belegen.</li>
            <li><strong>Anthropic, OpenAI, Google Gemini</strong> — KI-Textanalyse. Keine Daten werden zum Modell-Training verwendet.</li>
            <li><strong>Stripe Payments Europe Ltd</strong> — Abo-Zahlungen. Wir sehen nur die letzten 4 Kartennummer-Ziffern.</li>
            <li><strong>Bundesamt für Statistik (BFS)</strong> — Firmen-Adressanreicherung via UID-Register (nur öffentliche Daten).</li>
          </ul>

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">5. WhatsApp-spezifische Verarbeitung</h2>
          <p>
            Wenn du unseren WhatsApp-Bot nutzt, sendest du Bilder von Belegen an
            unsere Meta WhatsApp Business Nummer. Meta liefert diese Nachrichten
            an unseren Server aus. Wir verarbeiten:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Deine Telefonnummer (verknüpft mit deinem User-Konto).</li>
            <li>Bildinhalte deiner Belege (OCR-Erkennung → Speicherung als Beleg).</li>
            <li>Textnachrichten (nur Kommandos wie <code>/firma 1</code>).</li>
          </ul>
          <p>
            Meta-seitig gilt zusätzlich die <a href="https://www.whatsapp.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-indigo-600">WhatsApp-Datenschutzerklärung</a>.
            Deine WhatsApp-Nummer wird gelöscht, sobald du sie unter <em>Einstellungen → WhatsApp entkoppeln</em> löschst
            (oder eine Datenlöschung nach §7 anforderst).
          </p>

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">6. Speicherdauer</h2>
          <p>
            Buchhaltungsdaten unterliegen der Schweizer Aufbewahrungspflicht von <strong>10 Jahren</strong> (Art. 958f OR).
            Wir speichern deine Daten daher mindestens für diese Frist. WhatsApp-Metadaten (Telefonnummer,
            Sessions) löschen wir automatisch nach 24 Monaten Inaktivität.
          </p>

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">7. Deine Rechte</h2>
          <p>Du hast das Recht auf:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Auskunft über gespeicherte Daten (kostenlos, 1× pro Jahr).</li>
            <li>Berichtigung falscher Daten.</li>
            <li>Löschung (soweit keine Aufbewahrungspflicht besteht) — siehe <Link to="/data-deletion" className="text-indigo-600">Anleitung zur Datenlöschung</Link>.</li>
            <li>Datenportabilität (Export als JSON/CSV).</li>
            <li>Widerspruch gegen bestimmte Verarbeitungen.</li>
          </ul>
          <p>
            Anfragen richte bitte an <a href="mailto:datenschutz@ch-buchhaltung.ch" className="text-indigo-600">datenschutz@ch-buchhaltung.ch</a>.
            Wir antworten innerhalb von 30 Tagen.
          </p>

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">8. Server-Standort</h2>
          <p>
            Unsere Server stehen in Schweizer und EU-Rechenzentren. Zu Drittländer-Übermittlungen kommt es nur bei
            den in §4 genannten US-Anbietern (Anthropic, OpenAI, Stripe) — jeweils auf Basis von Standardvertragsklauseln
            der EU-Kommission bzw. dem EU-US Data Privacy Framework.
          </p>

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">9. Aufsichtsbehörde</h2>
          <p>
            Beschwerderecht beim Eidgenössischen Datenschutz- und Öffentlichkeitsbeauftragten (EDÖB):
            <br /><a href="https://www.edoeb.admin.ch" target="_blank" rel="noopener noreferrer" className="text-indigo-600">www.edoeb.admin.ch</a>
          </p>

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">10. Änderungen</h2>
          <p>
            Wir dürfen diese Datenschutzerklärung anpassen. Wesentliche Änderungen kommunizieren wir per E-Mail
            an dein Konto.
          </p>
        </section>

        <footer className="mt-16 pt-6 border-t border-slate-200 text-sm text-slate-500">
          <p>Fragen? Schreib uns an <a href="mailto:info@ch-buchhaltung.ch" className="text-indigo-600">info@ch-buchhaltung.ch</a>.</p>
        </footer>
      </main>
    </div>
  );
}
